import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  ScrollView,
  TextInput,
  Dimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from '../components/DirectionalIcon';
import { ScreenLayout, Header, FormHeader, Modal } from '../components';
import { useGoBack } from '../hooks/useGoBack';
import { CalendarMonthView, EventQuickViewModal } from '../components/calendar';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useAppStore } from '../store/appStore';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { colors, spacing, typography } from '../theme';
import { useThemedStyles } from '../hooks/useThemedStyles';

const { width: screenWidth } = Dimensions.get('window');
const isMobileBrowser = Platform.OS === 'web' && (screenWidth < 768);

export const BusinessCalendarScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, showNotification, activeTab, setActiveTab, routeParams } = useAppContext();
  const goBack = useGoBack('home');
  const { currentBusiness, user } = useAppStore();
  const { t } = useLocale();
  const rtl = useRTLStyles();

  const [calendars, setCalendars] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [calendarsMap, setCalendarsMap] = useState<Map<string, any>>(new Map());
  
  // View state - Force day view on mobile browser and mobile app
  const [viewMode, setViewMode] = useState<'month' | 'day' | 'list'>(
    isMobileBrowser || Platform.OS !== 'web' ? 'day' : 'month'
  );
  const [viewDate, setViewDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<Date | null>(
    isMobileBrowser || Platform.OS !== 'web' ? new Date() : null // Initialize with today on mobile
  );
  
  // Floating calendar picker state
  const [showCalendarPicker, setShowCalendarPicker] = useState(false);
  const [calendarPickerDate, setCalendarPickerDate] = useState(new Date());
  
  // Filter state
  const [selectedCalendarId, setSelectedCalendarId] = useState<string | null>(null); // null = all calendars
  const [filterStatus, setFilterStatus] = useState<'all' | 'upcoming' | 'past' | 'today'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCalendarDropdown, setShowCalendarDropdown] = useState(false);
  const [filterButtonLayout, setFilterButtonLayout] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const filterButtonRef = useRef<TouchableOpacity>(null);

  // Google-Calendar-style quick view — tapping an event in the month grid opens
  // this instead of navigating straight to the full editor (see EventQuickViewModal.tsx).
  const [quickViewEvent, setQuickViewEvent] = useState<any | null>(null);

  // Removed event creation modal - now navigates directly to create screen
  
  // Use state for dimensions to handle window resize on web
  const [dimensions, setDimensions] = useState(Dimensions.get('window'));
  const screenWidth = dimensions.width;
  const screenHeight = dimensions.height;
  
  // Handle window resize on web
  useEffect(() => {
    if (Platform.OS === 'web') {
      const updateDimensions = () => {
        if (typeof window !== 'undefined') {
          // Use window.innerWidth/innerHeight for accurate viewport dimensions on web
          setDimensions({
            width: window.innerWidth,
            height: window.innerHeight,
          });
        } else {
          setDimensions(Dimensions.get('window'));
        }
      };
      
      // Initial update
      updateDimensions();
      
      // Listen for window resize
      if (typeof window !== 'undefined') {
        window.addEventListener('resize', updateDimensions);
        return () => {
          window.removeEventListener('resize', updateDimensions);
        };
      }
    }
  }, []);

  // Set active tab to 'calendar' when this screen loads
  useEffect(() => {
    setActiveTab('calendar');
  }, [setActiveTab]);

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  // Load calendars
  const loadCalendars = useCallback(async () => {
    if (!currentBusiness?._id) {
      setLoading(false);
      return;
    }

    try {
      const response = await apiService.getCalendars({
        businessId: currentBusiness._id,
      });

      if (response.statusCode === 1000 && response.data) {
        const calendarsArray = Array.isArray(response.data) ? response.data : [response.data];
        setCalendars(calendarsArray);
        
        // Build calendars map for quick lookup
        const map = new Map<string, any>();
        calendarsArray.forEach((cal: any) => {
          map.set(cal._id, cal);
        });
        setCalendarsMap(map);
        
        // If calendarId is provided in routeParams, select it
        const calendarIdFromRoute = routeParams?.calendarId;
        if (calendarIdFromRoute) {
          // Verify the calendar exists in the loaded calendars
          const calendarExists = calendarsArray.some((cal: any) => cal._id === calendarIdFromRoute);
          if (calendarExists) {
            setSelectedCalendarId(calendarIdFromRoute);
          }
        }
      }
    } catch (error: any) {
      console.error('Error loading calendars:', error);
      showNotification('error', 'Error', t('calendar.business.loadCalendarsFailed'));
    } finally {
      setLoading(false);
    }
  }, [currentBusiness, showNotification, routeParams]);

  // Load events
  const loadEvents = useCallback(async () => {
    if (!currentBusiness?._id) {
      return;
    }

    try {
      setEventsLoading(true);
      // Load all events for the business, not just current month range
      // Set a wide date range to get all events
      const startDate = new Date();
      startDate.setFullYear(startDate.getFullYear() - 1); // 1 year ago
      startDate.setHours(0, 0, 0, 0);
      
      const endDate = new Date();
      endDate.setFullYear(endDate.getFullYear() + 1); // 1 year ahead
      endDate.setHours(23, 59, 59, 59);

      const response = await apiService.getEvents({
        businessId: currentBusiness._id,
        startDate,
        endDate,
      });

      if (response.statusCode === 1000 && response.data) {
        const eventsArray = response.data || [];
        setEvents(eventsArray);
        console.log('[Calendar] Loaded events:', eventsArray.length, 'events from', startDate.toISOString(), 'to', endDate.toISOString());
      }
    } catch (error: any) {
      console.error('Error loading events:', error);
      // Don't show error notification for event loading failures - it's not critical
      // showNotification('error', 'Error', 'Failed to load events');
    } finally {
      setEventsLoading(false);
    }
  }, [currentBusiness, showNotification]); // Removed viewDate dependency to load all events once

  useEffect(() => {
    loadCalendars();
  }, [loadCalendars]);

  useEffect(() => {
    if (!loading) {
      loadEvents();
    }
  }, [loadEvents, loading]);

  // Filter events
  const filteredEvents = useMemo(() => {
    let filtered = [...events];

    // Filter by selected calendar
    if (selectedCalendarId) {
      filtered = filtered.filter((e: any) => 
        e.calendarId === selectedCalendarId
      );
    }

    // Filter by status
    const now = new Date();
    if (filterStatus === 'upcoming') {
      filtered = filtered.filter((e: any) => new Date(e.startDate) >= now);
    } else if (filterStatus === 'past') {
      filtered = filtered.filter((e: any) => new Date(e.startDate) < now);
    } else if (filterStatus === 'today') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      filtered = filtered.filter((e: any) => {
        const eventDate = new Date(e.startDate);
        return eventDate >= today && eventDate < tomorrow;
      });
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((e: any) => 
        e.title?.toLowerCase().includes(query) ||
        e.description?.toLowerCase().includes(query)
      );
    }

    // Sort by start date
    filtered.sort((a: any, b: any) => 
      new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
    );

    return filtered;
  }, [events, selectedCalendarId, filterStatus, searchQuery]);

  // Get events for a specific date
  const getDateEvents = useCallback((date: Date) => {
    const dateStr = date.toDateString();
    return filteredEvents.filter((e: any) => 
      new Date(e.startDate).toDateString() === dateStr
    );
  }, [filteredEvents]);

  // Select calendar filter
  const selectCalendar = (calendarId: string | null) => {
    setSelectedCalendarId(calendarId);
    setShowCalendarDropdown(false);
    setFilterButtonLayout(null);
  };

  // Render compact event card
  const renderEventCard = (event: any) => {
    const calendar = event.calendarId ? calendarsMap.get(event.calendarId) : null;
    const calendarColor = calendar?.color || colors.primary;
    const eventDate = new Date(event.startDate);
    const eventEndDate = new Date(event.endDate);
    const isPast = eventDate < new Date();
    const isToday = eventDate.toDateString() === new Date().toDateString();

    return (
      <TouchableOpacity
        key={event._id}
        style={[
          styles.eventCard,
          { borderLeftColor: calendarColor },
          isPast && styles.eventCardPast,
        ]}
        onPress={() => {
          navigateTo('event-edit', event._id);
        }}
        activeOpacity={0.7}
      >
        <View style={[styles.eventCardHeader, { flexDirection: rtl.rowDirection }]}>
          <View style={[styles.eventCalendarTag, { backgroundColor: calendarColor + '20', flexDirection: rtl.rowDirection }]}>
            <View style={[styles.eventCalendarDot, { backgroundColor: calendarColor }]} />
            <Text style={[styles.eventCalendarName, { color: calendarColor }]} numberOfLines={1}>
              {calendar?.name || t('calendar.business.unknownCalendar')}
            </Text>
          </View>
          <Text style={styles.eventDate}>
            {eventDate.toLocaleDateString('en-US', { 
              month: 'short', 
              day: 'numeric',
              ...(eventDate.getFullYear() !== new Date().getFullYear() && { year: 'numeric' })
            })}
          </Text>
        </View>
        
        <Text style={styles.eventTitle} numberOfLines={2}>
          {event.title || t('calendar.business.untitledEvent')}
        </Text>
        
        <View style={[styles.eventTimeRow, { flexDirection: rtl.rowDirection }]}>
          <Ionicons name="time-outline" size={14} color={colors.text.secondary} />
          <Text style={styles.eventTime}>
            {eventDate.toLocaleTimeString('en-US', { 
              hour: 'numeric', 
              minute: '2-digit', 
              hour12: true 
            })}
            {eventEndDate && ` - ${eventEndDate.toLocaleTimeString('en-US', { 
              hour: 'numeric', 
              minute: '2-digit', 
              hour12: true 
            })}`}
          </Text>
        </View>
        
        {event.description && (
          <Text style={styles.eventDescription} numberOfLines={2}>
            {event.description}
          </Text>
        )}
        
        {event.location && (
          <View style={[styles.eventLocationRow, { flexDirection: rtl.rowDirection }]}>
            <Ionicons name="location-outline" size={14} color={colors.text.secondary} />
            <Text style={styles.eventLocation} numberOfLines={1}>
              {event.location}
            </Text>
          </View>
        )}
      </TouchableOpacity>
    );
  };

  // Render list view
  const renderListView = () => {
    if (eventsLoading) {
      return (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      );
    }

    if (filteredEvents.length === 0) {
      return (
        <View style={styles.emptyContainer}>
          <Ionicons name="calendar-outline" size={48} color={colors.text.secondary} />
          <Text style={styles.emptyText}>{t('calendar.business.noEventsFound')}</Text>
          <Text style={styles.emptySubtext}>
            {searchQuery || selectedCalendarId || filterStatus !== 'all'
              ? t('calendar.business.tryAdjustingFilters')
              : t('calendar.business.createFirstEvent')}
          </Text>
        </View>
      );
    }

    return (
      <ScrollView style={styles.listContainer} showsVerticalScrollIndicator={false}>
        {filteredEvents.map((event: any) => renderEventCard(event))}
      </ScrollView>
    );
  };

  // Render month view
  const renderMonthView = () => {
    // Convert events to CalendarMonthView format (for backward compatibility)
    const eventsByDate = new Map<string, number>();
    filteredEvents.forEach((event: any) => {
      const dateKey = new Date(event.startDate).toISOString().split('T')[0];
      eventsByDate.set(dateKey, (eventsByDate.get(dateKey) || 0) + 1);
    });

    const monthEvents = Array.from(eventsByDate.entries()).map(([dateKey, count]) => ({
      date: new Date(dateKey),
      count,
    }));

    // SIMPLIFIED: Calculate available space for the entire calendar component
    const headerHeight = Platform.OS === 'web' ? 180 : 100;
    const topBarHeight = 60;
    const filtersHeight = 0; // Filter section removed, calendar dropdown is floating
    const containerPadding = spacing.md * 2;
    
    // Get available height in content area - use more of the space
    let availableHeight: number;
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      // Use more of the window height - reduce reserved space
      availableHeight = window.innerHeight - headerHeight - topBarHeight - filtersHeight - (containerPadding / 2);
    } else {
      const bottomTabsHeight = 80;
      availableHeight = screenHeight - headerHeight - topBarHeight - filtersHeight - bottomTabsHeight - (containerPadding / 2);
    }
    
    // Get available width in content area
    let containerWidth: number;
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      containerWidth = window.innerWidth - 355; // Left pane width
    } else {
      containerWidth = screenWidth;
    }
    
    // Calendar component structure
    const calendarHeaderHeight = 50;
    const dayHeadersHeight = 30;
    const maxRows = 6;
    const calendarPadding = spacing.md; // Reduced padding
    const calendarPaddingHorizontal = spacing.md * 2;
    
    // Calculate available space for day cells - use more of the available height
    const availableForRows = availableHeight - calendarHeaderHeight - dayHeadersHeight - (calendarPadding * 2);
    const availableForColumns = containerWidth - calendarPaddingHorizontal;
    
    // Calculate day cell size to fill available space
    // Ensure 7 days per row (14.28% width each)
    const dayCellWidth = (availableForColumns / 7); // 7 columns - each day gets 1/7 of width
    const dayCellHeight = availableForRows / maxRows; // 6 rows - each day gets 1/6 of available height
    
    // Use the smaller dimension to ensure it fits both width and height
    // This ensures all 7 days fit per row and all 6 rows fit in the height
    const dayCellSize = Math.min(dayCellWidth, dayCellHeight);
    
    // Calculate total calendar height based on day cell size
    const totalCalendarHeight = calendarHeaderHeight + dayHeadersHeight + (maxRows * dayCellSize) + (calendarPadding * 2);

    // Debug logging
    if (Platform.OS === 'web') {
      console.log('[Calendar] Sizing calculations:', {
        windowHeight: typeof window !== 'undefined' ? window.innerHeight : 'N/A',
        windowWidth: typeof window !== 'undefined' ? window.innerWidth : 'N/A',
        availableHeight,
        availableForColumns,
        dayCellWidth,
        dayCellHeight,
        dayCellSize,
        totalCalendarHeight,
        containerWidth,
        availableForRows,
      });
    }

    return (
      <View style={styles.monthViewContainer}>
        <View style={{ height: totalCalendarHeight, width: '100%' }}>
          <CalendarMonthView
            currentDate={viewDate}
            selectedDate={selectedDay || undefined}
            events={monthEvents}
            fullEvents={filteredEvents}
            calendarsMap={calendarsMap}
            onEventPress={(event) => {
              setQuickViewEvent(event);
            }}
            dayCellSize={dayCellSize}
            onDateSelect={(date: Date) => {
              setSelectedDay(date);
              setViewMode('day');
            }}
            onPreviousMonth={() => {
              const prev = new Date(viewDate);
              prev.setMonth(prev.getMonth() - 1);
              setViewDate(prev);
            }}
            onNextMonth={() => {
              const next = new Date(viewDate);
              next.setMonth(next.getMonth() + 1);
              setViewDate(next);
            }}
          />
        </View>
      </View>
    );
  };

  // Render day view (reuse CalendarBookingScreen logic)
  const renderDayView = () => {
    // Ensure selectedDay is set (fallback to today)
    const displayDay = selectedDay || new Date();
    const dayEvents = getDateEvents(displayDay);
    
    // Navigation handlers
    const handlePreviousDay = () => {
      const prevDay = new Date(displayDay);
      prevDay.setDate(prevDay.getDate() - 1);
      setSelectedDay(prevDay);
    };
    
    const handleNextDay = () => {
      const nextDay = new Date(displayDay);
      nextDay.setDate(nextDay.getDate() + 1);
      setSelectedDay(nextDay);
    };
    
    const handleToday = () => {
      setSelectedDay(new Date());
    };
    
    return (
      <View style={styles.dayViewWrapper}>
        {/* Day Navigation Controls - Show on mobile app and mobile browser */}
        {(Platform.OS !== 'web' || isMobileBrowser) && (
          <View style={[styles.dayNavigation, { flexDirection: rtl.rowDirection }]}>
            <TouchableOpacity
              style={styles.dayNavButton}
              onPress={handlePreviousDay}
              activeOpacity={0.7}
            >
              <DirectionalIcon name="chevron-back" size={24} color={colors.primary} />
            </TouchableOpacity>
            
            <TouchableOpacity
              style={styles.dayNavDateButton}
              onPress={() => {
                setCalendarPickerDate(displayDay);
                setShowCalendarPicker(true);
              }}
              activeOpacity={0.7}
            >
              <Ionicons name="calendar-outline" size={18} color={colors.primary} style={rtl.marginEnd(spacing.xs)} />
              <Text style={styles.dayNavDateText}>
                {displayDay.toLocaleDateString('en-US', { 
                  weekday: 'short',
                  month: 'short', 
                  day: 'numeric',
                  year: 'numeric'
                })}
              </Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={styles.dayNavButton}
              onPress={handleNextDay}
              activeOpacity={0.7}
            >
              <DirectionalIcon name="chevron-forward" size={24} color={colors.primary} />
            </TouchableOpacity>
          </View>
        )}
        
        <ScrollView style={styles.dayViewContainer}>
          {Platform.OS === 'web' && (
            <View style={styles.dayViewHeader}>
              <Text style={styles.dayViewDate}>
                {displayDay.toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  month: 'long', 
                  day: 'numeric',
                  year: 'numeric'
                })}
              </Text>
            </View>
          )}
          
          {dayEvents.length === 0 ? (
            <View style={styles.emptyContainer}>
              <Ionicons name="calendar-outline" size={48} color={colors.text.secondary} />
              <Text style={styles.emptyText}>{t('calendar.business.noEventsForDay')}</Text>
            </View>
          ) : (
            <View style={styles.dayEventsContainer}>
              {dayEvents.map((event: any) => renderEventCard(event))}
            </View>
          )}
        </ScrollView>
      </View>
    );
  };

  return (
    <ScreenLayout
      header={
        <View style={{ position: 'relative', width: '100%' }}>
          <Header
            showNotifications={true}
            showMessages={true}
            onNotificationPress={() => navigateTo('notifications')}
            onMessagePress={() => navigateTo('chat')}
            showNavigation={Platform.OS === 'web'}
            activeTab={activeTab}
            onTabPress={handleTabPress}
          />
        </View>
      }
      showBottomTabs={Platform.OS !== 'web'}
      onTabPress={handleTabPress}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      activeTab={activeTab}
      formHeader={
        <FormHeader
          title="Calendar"
          onBack={goBack}
          actions={[
            {
              icon: showCalendarDropdown ? 'filter' : 'filter-outline',
              onPress: () => {
                if (filterButtonRef.current) {
                  filterButtonRef.current.measureInWindow((x, y, width, height) => {
                    setFilterButtonLayout({ x, y, width, height });
                  });
                }
                setShowCalendarDropdown(!showCalendarDropdown);
              },
              active: showCalendarDropdown || !!selectedCalendarId,
              buttonRef: filterButtonRef,
              label: 'Filter Calendars',
              accessibilityLabel: 'Filter calendars',
            },
            { icon: 'settings-outline', onPress: () => navigateTo('calendars'), label: 'Manage Calendars', accessibilityLabel: 'Manage calendars' },
            {
              icon: 'add',
              primary: true,
              onPress: () => {
                // Find default calendar if "all" is selected, otherwise use selected calendar
                let calendarIdToUse = selectedCalendarId;
                if (!calendarIdToUse && calendars.length > 0) {
                  const defaultCalendar = calendars.find((cal: any) => cal.isDefault === true);
                  calendarIdToUse = defaultCalendar?._id || calendars[0]?._id || null;
                }

                // Navigate directly to create event screen with calendar ID
                navigateTo('event-create', calendarIdToUse || undefined, {
                  selectedDate: selectedDay?.toISOString(),
                  calendarId: calendarIdToUse,
                });
              },
              accessibilityLabel: 'Create event',
            },
          ]}
        />
      }
    >
      <View style={styles.container}>
        {/* Search + View Mode Toggle — functional list-filtering UI, not
            header chrome, so it's a compact single row (no more duplicate
            filter/settings/add icons — those now live in FormHeader above). */}
        <View style={[styles.topBar, { flexDirection: rtl.rowDirection }]}>
          <View style={[styles.searchContainer, { flexDirection: rtl.rowDirection }]}>
            <Ionicons name="search-outline" size={20} color={colors.text.secondary} style={rtl.marginEnd(spacing.sm)} />
            <TextInput
              style={styles.searchInput}
              placeholder={t('calendar.business.searchPlaceholder')}
              value={searchQuery}
              onChangeText={setSearchQuery}
              placeholderTextColor={colors.text.secondary}
            />
            {searchQuery.length > 0 && (
              <TouchableOpacity
                onPress={() => setSearchQuery('')}
                style={styles.clearButton}
              >
                <Ionicons name="close-circle" size={20} color={colors.text.secondary} />
              </TouchableOpacity>
            )}
          </View>

          {/* View Mode Toggle - Only show on desktop web, not mobile browser */}
          {Platform.OS === 'web' && !isMobileBrowser && (
            <View style={[styles.viewModeToggle, { flexDirection: rtl.rowDirection }]}>
              <TouchableOpacity
                style={[styles.viewModeButton, viewMode === 'month' && styles.viewModeButtonActive]}
                onPress={() => setViewMode('month')}
              >
                <Ionicons name="grid-outline" size={18} color={viewMode === 'month' ? colors.primary : colors.text.secondary} />
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.viewModeButton, viewMode === 'day' && styles.viewModeButtonActive]}
                onPress={() => {
                  if (!selectedDay) setSelectedDay(new Date());
                  setViewMode('day');
                }}
              >
                <Ionicons name="calendar-outline" size={18} color={viewMode === 'day' ? colors.primary : colors.text.secondary} />
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.viewModeButton, viewMode === 'list' && styles.viewModeButtonActive]}
                onPress={() => setViewMode('list')}
              >
                <Ionicons name="list-outline" size={18} color={viewMode === 'list' ? colors.primary : colors.text.secondary} />
              </TouchableOpacity>
            </View>
          )}
        </View>

        {/* Calendar Filter Dropdown Menu - Floating */}
        {showCalendarDropdown && filterButtonLayout && (
          <>
            <TouchableOpacity
              style={styles.overlayBackground}
              activeOpacity={1}
              onPress={() => {
                setShowCalendarDropdown(false);
                setFilterButtonLayout(null);
              }}
            />
            <View style={[styles.calendarMenuOverlay, {
              top: "30px",//filterButtonLayout.y + filterButtonLayout.height, // Directly below filter button (no gap)
              right: screenWidth - filterButtonLayout.x - filterButtonLayout.width, // Right-aligned with filter button
            }]}>
              <View style={styles.calendarMenu}>
                <TouchableOpacity
                  style={[
                    styles.calendarMenuItem,
                    { flexDirection: rtl.rowDirection },
                    !selectedCalendarId && styles.calendarMenuItemActive,
                  ]}
                  onPress={() => selectCalendar(null)}
                >
                  <Ionicons
                    name="grid-outline"
                    size={18}
                    color={!selectedCalendarId ? colors.white : colors.primary}
                  />
                  <Text style={[
                    styles.calendarMenuItemText,
                    !selectedCalendarId && styles.calendarMenuItemTextActive,
                  ]}>
                    {t('calendar.business.allCalendars')}
                  </Text>
                </TouchableOpacity>
                {calendars.map((cal: any) => (
                  <TouchableOpacity
                    key={cal._id}
                    style={[
                      styles.calendarMenuItem,
                      { flexDirection: rtl.rowDirection },
                      selectedCalendarId === cal._id && styles.calendarMenuItemActive,
                    ]}
                    onPress={() => selectCalendar(cal._id)}
                  >
                    <View style={[styles.calendarMenuItemDot, { backgroundColor: cal.color }]} />
                    <Text style={[
                      styles.calendarMenuItemText,
                      selectedCalendarId === cal._id && styles.calendarMenuItemTextActive,
                    ]}>
                      {cal.name}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          </>
        )}

        {/* Content */}
        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={colors.primary} />
          </View>
        ) : (
          <>
            {viewMode === 'month' && renderMonthView()}
            {viewMode === 'day' && renderDayView()}
            {viewMode === 'list' && renderListView()}
          </>
        )}
      </View>


      {/* Floating Calendar Picker Modal */}
      <Modal
        visible={showCalendarPicker}
        onClose={() => setShowCalendarPicker(false)}
        title={t('calendar.business.selectDate')}
        size={Platform.OS !== 'web' || isMobileBrowser ? 'large' : 'medium'}
        scrollable={false}
      >
        <View style={[styles.modalContent, Platform.OS !== 'web' && styles.modalContentMobile]}>
          <CalendarMonthView
            currentDate={calendarPickerDate}
            selectedDate={selectedDay || undefined}
            events={[]}
            onDateSelect={(date: Date) => {
              setSelectedDay(date);
              setShowCalendarPicker(false);
            }}
            onPreviousMonth={() => {
              const prev = new Date(calendarPickerDate);
              prev.setMonth(prev.getMonth() - 1);
              setCalendarPickerDate(prev);
            }}
            onNextMonth={() => {
              const next = new Date(calendarPickerDate);
              next.setMonth(next.getMonth() + 1);
              setCalendarPickerDate(next);
            }}
          />
        </View>
      </Modal>

      <EventQuickViewModal
        visible={!!quickViewEvent}
        event={quickViewEvent}
        businessId={currentBusiness?._id}
        onClose={() => setQuickViewEvent(null)}
        onEdit={(eventId) => {
          setQuickViewEvent(null);
          navigateTo('event-edit', eventId);
        }}
        onDeleted={() => {
          setQuickViewEvent(null);
          loadEvents();
        }}
        onNotify={showNotification}
      />
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  topBar: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    gap: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  searchContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.background.secondary,
    borderRadius: spacing.borderRadius.md,
    paddingHorizontal: spacing.md,
    height: 40,
  },
  searchInput: {
    flex: 1,
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.primary,
  },
  clearButton: {
    padding: spacing.xs,
  },
  viewModeToggle: {
    flexDirection: 'row',
    backgroundColor: colors.background.secondary,
    borderRadius: spacing.borderRadius.md,
    padding: spacing.xs / 2,
    gap: spacing.xs / 2,
  },
  viewModeButton: {
    padding: spacing.sm,
    borderRadius: spacing.borderRadius.sm,
  },
  viewModeButtonActive: {
    backgroundColor: colors.brand.primary,
  },
  filtersContainer: {
    backgroundColor: colors.background.surface,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    padding: spacing.md,
  },
  filterSection: {
    marginBottom: spacing.md,
  },
  filterLabel: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.secondary,
    marginBottom: spacing.sm,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  dropdownButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: spacing.borderRadius.md,
    backgroundColor: colors.background.secondary,
    borderWidth: 1,
    borderColor: colors.border,
    minHeight: 40,
  },
  dropdownButtonText: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.primary,
    flex: 1,
  },
  overlayBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    zIndex: 999,
  },
  calendarMenuOverlay: {
    position: 'absolute',
    width: isMobileBrowser ? 200 : (Platform.OS === 'web' ? 220 : 200),
    zIndex: 1000,
  },
  calendarMenu: {
    backgroundColor: colors.background.surfaceRaised,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 8,
    paddingVertical: 2, // Minimal padding at top
  },
  calendarMenuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    gap: spacing.sm,
  },
  calendarMenuItemActive: {
    backgroundColor: colors.primary,
  },
  calendarMenuItemDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  calendarMenuItemText: {
    fontSize: 14,
    color: colors.text.primary,
    fontWeight: '500',
  },
  calendarMenuItemTextActive: {
    color: colors.white,
  },
  monthViewContainer: {
    flex: 1,
    padding: spacing.md,
    width: '100%',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  dayViewWrapper: {
    flex: 1,
  },
  dayViewContainer: {
    flex: 1,
  },
  dayNavigation: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  dayNavButton: {
    padding: spacing.sm,
    borderRadius: spacing.borderRadius.md,
    backgroundColor: colors.background.secondary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  dayNavDateButton: {
    flex: 1,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dayNavDateText: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
  },
  dayViewHeader: {
    padding: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  dayViewDate: {
    fontSize: typography.fontSize.xl,
    fontFamily: typography.fontFamily.bold,
    color: colors.text.primary,
  },
  dayEventsContainer: {
    padding: spacing.md,
    gap: spacing.sm,
  },
  listContainer: {
    flex: 1,
    padding: spacing.md,
  },
  eventCard: {
    backgroundColor: colors.background.surface,
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    borderLeftWidth: 4,
    padding: spacing.md,
    marginBottom: spacing.sm,
  },
  eventCardPast: {
    opacity: 0.6,
  },
  eventCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  eventCalendarTag: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs / 2,
    borderRadius: spacing.borderRadius.sm,
    gap: spacing.xs,
  },
  eventCalendarDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  eventCalendarName: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.semibold,
  },
  eventDate: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.secondary,
  },
  eventTitle: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
    marginBottom: spacing.xs,
  },
  eventTimeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.xs,
  },
  eventTime: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
  },
  eventDescription: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
    marginBottom: spacing.xs,
  },
  eventLocationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  eventLocation: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  emptyText: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
    marginTop: spacing.md,
  },
  emptySubtext: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
    marginTop: spacing.xs,
    textAlign: 'center',
  },
  modalContent: {
    padding: spacing.md,
  },
  modalContentMobile: {
    paddingHorizontal: spacing.sm,
    paddingTop: spacing.xs,
    paddingBottom: spacing.md, // Minimal padding - let calendar size naturally
    minHeight: 480, // Ensure enough height for full calendar (header ~60px + day headers ~35px + 6 rows ~360px + padding ~25px)
  },
  modalText: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.primary,
    marginBottom: spacing.md,
  },
  modalButton: {
    backgroundColor: colors.primary,
    padding: spacing.md,
    borderRadius: spacing.borderRadius.md,
    alignItems: 'center',
  },
  modalButtonText: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.white,
  },
});

